import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { LogIn, UserPlus, Loader2, Mail, Lock, User, Shield } from 'lucide-react';
import { useAuth } from '../App';
import { toast } from 'sonner';

const Login = () => {
  const [isSignup, setIsSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, user } = useAuth();

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Check if coming from admin link
  const isAdminLogin = location.search.includes('admin=true');

  useEffect(() => {
    if (user) {
      console.log('[LOGIN REDIRECT] User detected:', user.email, 'role:', user.role);
      
      // CRITICAL FIX: If accessing admin login page, always go to admin
      if (isAdminLogin) {
        console.log('[LOGIN REDIRECT] Admin login page accessed, redirecting to /admin');
        navigate('/admin');
        return;
      }
      
      // Otherwise redirect based on role
      if (user.role === 'admin') {
        console.log('[LOGIN REDIRECT] Admin user, redirecting to /admin');
        navigate('/admin');
      } else {
        console.log('[LOGIN REDIRECT] Regular user, redirecting to /app/play');
        navigate('/app/play');
      }
    }
  }, [user, navigate, isAdminLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isSignup ? '/api/auth/signup' : '/api/auth/login';
      const payload = isSignup 
        ? { email: formData.email, password: formData.password, name: formData.name }
        : { email: formData.email, password: formData.password };

      console.log('[LOGIN] Submitting to:', `${API_URL}${endpoint}`);
      console.log('[LOGIN] Email:', formData.email);
      console.log('[LOGIN] Type:', isSignup ? 'SIGNUP' : 'LOGIN');

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      console.log('[LOGIN] Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('[LOGIN] SUCCESS! User:', data.user.email, 'Role:', data.user.role);
        setUser(data.user);
        toast.success(isSignup ? 'Account created!' : 'Welcome back!');
        // useEffect will handle navigation
      } else {
        const errorText = await response.text();
        console.error('[LOGIN] ERROR:', response.status, errorText);
        let errorMsg;
        try {
          const errorData = JSON.parse(errorText);
          errorMsg = errorData.detail;
        } catch {
          errorMsg = errorText || 'Authentication failed';
        }
        toast.error(errorMsg, { duration: 5000 });
      }
    } catch (error) {
      console.error('[LOGIN] EXCEPTION:', error);
      toast.error(`Connection failed: ${error.message}`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#071A14] via-[#0A241C] to-[#0B0F14] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <h1 className="text-3xl font-heading font-bold">
              LUCK <span className="text-[#C9A227]">or</span> SKILLS
            </h1>
          </Link>
          <h2 className="text-2xl font-heading font-semibold mb-2">
            {isSignup ? 'Create Your Account' : 'Welcome Back'}
          </h2>
          <p className="text-foreground/60">
            {isSignup ? 'Start your poker training journey' : 'Continue your training'}
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-[#121923] border-2 border-[#C9A227]/20 rounded-xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name field (signup only) */}
            {isSignup && (
              <div>
                <label htmlFor="name" className="block text-sm font-semibold mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-foreground/40" />
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    data-testid="name-input"
                    className="w-full pl-11 pr-4 py-3 bg-[#0B0F14] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A227] focus:border-transparent text-foreground"
                  />
                </div>
              </div>
            )}

            {/* Email field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-foreground/40" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  data-testid="email-input"
                  className="w-full pl-11 pr-4 py-3 bg-[#0B0F14] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A227] focus:border-transparent text-foreground"
                />
              </div>
            </div>

            {/* Password field */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-foreground/40" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  data-testid="password-input"
                  className="w-full pl-11 pr-4 py-3 bg-[#0B0F14] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A227] focus:border-transparent text-foreground"
                />
              </div>
              {isSignup && (
                <p className="text-xs text-foreground/60 mt-1">Must be at least 6 characters</p>
              )}
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              data-testid={isSignup ? 'signup-submit-btn' : 'login-submit-btn'}
              className="w-full py-4 bg-[#C9A227] hover:bg-[#E1C25B] text-[#0B0F14] rounded-lg font-heading font-bold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {isSignup ? 'Creating Account...' : 'Signing In...'}
                </>
              ) : (
                <>
                  {isSignup ? <UserPlus className="w-5 h-5" /> : <LogIn className="w-5 h-5" />}
                  {isSignup ? 'Create Account' : 'Sign In'}
                </>
              )}
            </button>
          </form>

          {/* Toggle signup/login */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsSignup(!isSignup);
                setFormData({ email: '', password: '', name: '' });
              }}
              className="text-sm text-foreground/60 hover:text-[#C9A227] transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isSignup ? (
                <>Already have an account? <span className="font-semibold">Sign in</span></>
              ) : (
                <>Don't have an account? <span className="font-semibold">Sign up</span></>
              )}
            </button>
          </div>
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-sm text-foreground/60 hover:text-foreground transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
